import React, { createContext, useContext, useState, useEffect } from 'react'
import api from '../services/api'

const AuthContext = createContext()

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false)
  const [authReturnPath, setAuthReturnPath] = useState('/') // 登录成功后返回的路径
  const [isGuestMode, setIsGuestMode] = useState(false) // Guest mode 布尔值
  const [guestEmail, setGuestEmail] = useState(null) // Guest mode 的 email（用户输入后设置）

  // 检查本地存储中的用户信息
  useEffect(() => {
    const savedUser = localStorage.getItem('user')
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser))
      } catch (error) {
        console.error('Error parsing saved user:', error)
        localStorage.removeItem('user')
      }
    }
    setLoading(false)
  }, [])

  // 保存用户信息到本地存储
  const saveUser = (userData, token) => {
    setUser(userData)
    localStorage.setItem('user', JSON.stringify(userData))
    if (token) {
      localStorage.setItem('token', token)
    }
  }

  // 清除用户信息
  const clearUser = () => {
    setUser(null)
    localStorage.removeItem('user')
    localStorage.removeItem('token')
  }

  // 检查邮箱是否存在
  const checkEmail = async (email) => {
    try {
      const result = await api.checkEmail(email)
      return result
    } catch (error) {
      console.error('Check email error:', error)
      return { success: false, error: error.message }
    }
  }

  // 邮箱登录/注册
  const signInWithEmail = async (email, password) => {
    try {
      const response = await api.signIn(email, password)
      
      if (response.success) {
        saveUser(response.data.user, response.data.token)
        setIsAuthModalOpen(false)
        return { success: true, user: response.data.user }
      } else {
        return { success: false, error: response.message }
      }
    } catch (error) {
      console.error('Email sign in error:', error)
      return { success: false, error: error.message }
    }
  }

  // 邮箱注册
  const signUpWithEmail = async (email, password, name) => {
    try {
      const response = await api.signUp(email, password, name)
      
      if (response.success) {
        saveUser(response.data.user, response.data.token)
        setIsAuthModalOpen(false)
        return { success: true, user: response.data.user }
      } else {
        return { success: false, error: response.message }
      }
    } catch (error) {
      console.error('Email sign up error:', error)
      return { success: false, error: error.message }
    }
  }


  // 登出
  const signOut = () => {
    clearUser()
  }

  // 打开认证模态框
  const openAuthModal = (returnPath = '/') => {
    setAuthReturnPath(returnPath)
    setIsAuthModalOpen(true)
  }

  // 关闭认证模态框
  const closeAuthModal = () => {
    setIsAuthModalOpen(false)
  }

  // 设置 Guest Mode
  const setGuestMode = (enabled) => {
    setIsGuestMode(enabled)
    if (!enabled) {
      setGuestEmail(null) // 退出 guest mode 时清除 email
    }
    setIsAuthModalOpen(false)
  }

  // 设置 Guest Email（在 checkout 页面用户输入 email 后调用）
  const setGuestEmailAddress = (email) => {
    if (isGuestMode) {
      setGuestEmail(email)
    }
  }

  // 清除 Guest Mode
  const clearGuestMode = () => {
    setIsGuestMode(false)
    setGuestEmail(null)
  }

  // 更新用户信息
  const updateUserProfile = async (profileData) => {
    try {
      const response = await api.updateProfile(profileData)
      
      if (response.success) {
        const updatedUser = response.data.user
        saveUser(updatedUser, null) // 更新用户信息，不更新 token
        return { success: true, user: updatedUser }
      } else {
        return { success: false, error: response.message }
      }
    } catch (error) {
      console.error('Update profile error:', error)
      return { success: false, error: error.message }
    }
  }

  const value = {
    user,
    loading,
    isAuthenticated: !!user, // 添加isAuthenticated属性
    isAuthModalOpen,
    authReturnPath, // 登录成功后返回的路径
    // 只设返回路径、不打开 Header 里那个弹窗。
    // Checkout 自己渲染了一个 AuthModal（关闭时要跳回购物车），
    // 如果它调 openAuthModal 就会两个弹窗一起开。
    setAuthReturnPath,
    isGuestMode, // Guest mode 布尔值
    guestEmail, // Guest mode 的 email
    saveUser,
    clearUser,
    checkEmail,
    signInWithEmail,
    signUpWithEmail,
    signOut,
    openAuthModal,
    closeAuthModal,
    setGuestMode,
    setGuestEmailAddress,
    clearGuestMode,
    updateUserProfile
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
